import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DispatchService } from '@app/shared/services/dispatch.service';
import { RepeatOrderService } from '@app/shared/services/repeat-order.service';
import { OrderDataService } from '@app/shared/services/order-data.service';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';

interface Dispatch {
  dispatched: number;
  kgsBal: number;
}

@Component({
  selector: 'app-dispatch-from',
  templateUrl: './dispatch-from.component.html',
  styleUrls: ['./dispatch-from.component.css']
})
export class DispatchFromComponent implements OnInit {
  dispatchForm: FormGroup;
  dispatchId: string;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private Router:Router,
    private dispatchService: DispatchService,
    private repeatOrderService: RepeatOrderService,
    private orderDataService: OrderDataService
  ) { }

  ngOnInit(): void {
    // Get the ID from the route parameters
    this.route.params.subscribe(params => {
      this.dispatchId = params['id'];
      this.loadDispatchdispatchAndkgsbal();
    });

    this.initializeForm();
  }

  initializeForm(): void {
    this.dispatchForm = this.fb.group({

      orderKgs: [null, Validators.required],
      poNo: [null, Validators.required],
      clientName: [null, Validators.required],
      ticketNo: [null, Validators.required],
      dispatched: [null, Validators.required],
      kgsBal: [null, Validators.required],
      orderPc: [null, Validators.required],
      orderNo: [null, Validators.required],
      refNo: [null, Validators.required],
      jobName: [null, Validators.required],
      poNumber: [null, Validators.required],
      dispatchDetails: this.fb.array([this.createDispatchDetail()]) // Initialize with one dispatch pair
    });

    // Listen for changes to dispatches and recalculate dispatched and kgsBal
    // this.dispatchDetails.valueChanges.subscribe(() => {
    //   this.calculateDispatchedAndKgsBal();
    // });
  }




  loadDispatchdispatchAndkgsbal(): void {
    this.dispatchService.getDispatchBydispatchId(this.dispatchId).subscribe({
      next: (dispatchData: Dispatch) => {
        if (dispatchData) {
          // Populate the form with values (without changing non-dynamic fields)
          this.populateForm(dispatchData);
  
          // Get initial dispatched and kgsBal values from the fetched data
          const { dispatched, kgsBal } = dispatchData;
          const orderKgs = parseFloat(this.dispatchForm.get('orderKgs')?.value || '0');
  
          // Set initial dispatched and kgsBal values in the form
          this.dispatchForm.patchValue({
            dispatched: dispatched || 0,
            kgsBal: kgsBal || 0
          });
  
          // Log initial dispatched and kgsBal values for debugging
          console.log('Initial dispatched value:', dispatched);
          console.log('Initial kgsBal value:', kgsBal);
  
          // Listen to changes in dispatchDetails and dynamically update dispatched and kgsBal
          this.dispatchDetails.valueChanges.subscribe(() => {
            // Ensure dispatchDetails.controls is properly set before accessing it
            if (this.dispatchDetails && this.dispatchDetails.controls) {
              // Start by setting the total dispatched value to the initial dispatched value (as a number)
              let totalDispatched = parseInt(dispatched.toString()) || 0;  // Ensure it is treated as a number
              console.log('Starting totalDispatched with initial value:', totalDispatched);
          
              // Loop through each dispatch item and accumulate the new dispatched values
              this.dispatchDetails.controls.forEach((dispatchGroup: FormGroup, index: number) => {
                const dispValue = parseFloat(dispatchGroup.get('disp')?.value || '0');  // Ensure numeric value
                console.log(`Disp value from group ${index}:`, dispValue);  // Log disp value for debugging
          
                // Accumulate the dispatched value (ensuring no string concatenation)
                totalDispatched += dispValue;
              });
          
              // Log dispatched sum before updating the form
              console.log('Total dispatched sum of current disp values:', totalDispatched);
          
              // Update the form with the new total dispatched value
              this.dispatchForm.patchValue({ dispatched: totalDispatched });
              console.log('Updated dispatched value:', totalDispatched);
          
              // Calculate the remaining balance (kgsBal)
              const remainingBalance = orderKgs - totalDispatched;
          
              // Ensure kgsBal doesn't go below zero
              const safeRemainingBalance = Math.max(remainingBalance, 0);
          
              // Update the form with the remaining balance (kgsBal)
              this.dispatchForm.patchValue({ kgsBal: safeRemainingBalance });
              console.log('Remaining balance:', safeRemainingBalance);
          
              // Check if dispatched exceeds available kgsBal and warn the user
              if (totalDispatched > orderKgs) {
                alert('Dispatched quantity exceeds available balance!');
              }
            } else {
              console.error('dispatchDetails.controls is undefined or not properly initialized');
            }
          });
          
        } else {
          console.error('No dispatch found with the given ID');
        }
      },
      error: (err) => {
        console.error('Error fetching dispatch data', err);
      }
    });
  }

  loadDispatchData(): void {
    forkJoin({
      repeatOrders: this.repeatOrderService.getAllRepeatOrder(),
      unsavedOrders: this.orderDataService.getAllUnsavedOrders()
    }).subscribe({
      next: (data) => {
        const repeatOrders = Array.isArray(data.repeatOrders) ? data.repeatOrders : [];
        const unsavedOrders = Array.isArray(data.unsavedOrders) ? data.unsavedOrders : [];
        let dispatchData = repeatOrders.find(order => order._id === this.dispatchId) ||
                           unsavedOrders.find(order => order._id === this.dispatchId);
  
        if (dispatchData) {
          console.log('+', dispatchData);
          this.populateForm(dispatchData);
          // Check if the order is completed
          if (dispatchData.status === 'Closed') {
            alert('This order has already been completed!');
            this.dispatchForm.disable(); // Disable the form if completed
          }
        } else {
          console.error('No dispatch found with the given ID');
        }
      },
      error: (err) => {
        console.error('Error fetching dispatch data', err);
      }
    });
  }

  populateForm(dispatchData: any): void {
    this.dispatchForm.patchValue({
      orderKgs: parseFloat(dispatchData.orderKgs).toFixed(2),
      clientName: dispatchData.clientName,
      poNo: dispatchData.poNo,
      ticketNo: dispatchData.ticketno,
      orderPc: dispatchData.orderPcs || 0,
      orderNo: dispatchData.orderId || dispatchData.jssId,
      refNo: dispatchData.refNo,
      jobName: dispatchData.jobName,
      poNumber: dispatchData.poNo
    });
  
    // Populate dispatches if available
    if (dispatchData.dispatches && dispatchData.dispatches.length) {
      dispatchData.dispatches.forEach(() => {
        this.addDispatch(); // Add new dispatch dynamically
      });
    }
  
    // Calculate dispatched and kgsBal initially if there are dispatches
    // this.calculateDispatchedAndKgsBal();
    // this.loadDispatchdispatchAndkgsbal();
  }

  createDispatchDetail(): FormGroup {
    return this.fb.group({
      disp: [null, Validators.required],
      dispatchDate: [null, Validators.required]
    });
  }

  get dispatchDetails() {
    return this.dispatchForm.get('dispatchDetails') as FormArray;
  }

  addDispatch(): void {
    this.dispatchDetails.push(this.createDispatchDetail());
  }

  removeDispatch(): void {
    if (this.dispatchDetails.length > 1) {
      this.dispatchDetails.removeAt(this.dispatchDetails.length - 1);
    } else {
      alert('At least one dispatch pair is required.');
    }
  }

  // calculateDispatchedAndKgsBal(): void {
  //   // Calculate the sum of all dispatched values
  //   const dispatchedSum = this.dispatchDetails.controls.reduce((sum, dispatchGroup: FormGroup) => {
  //     const dispValue = dispatchGroup.get('disp')?.value || 0;
  //     return sum + dispValue;
  //   }, 0);

  //   // Update dispatched field
  //   this.dispatchForm.patchValue({ dispatched: dispatchedSum });

  //   // Calculate kgsBal
  //   const orderKgs = this.dispatchForm.get('orderKgs')?.value || 0;
  //   const kgsBal = orderKgs - dispatchedSum;

  //   // Update kgsBal field
  //   this.dispatchForm.patchValue({ kgsBal: kgsBal });
  // }

  onSubmit(): void {
    if (this.dispatchForm.valid) {
      // Add dispatchId to the form data to be sent to the backend
      const formData = {
        ...this.dispatchForm.value,
      };
  
      console.log('Form Data to be submitted: ', formData);
  
      // Send the form data along with the dispatchId to the service
      this.dispatchService.addDispatch(formData, this.dispatchId).subscribe(
        (res: any) => {
          console.log('Dispatch created successfully:', res);
          this.Router.navigateByUrl('/DispatchTable');  // This will redirect to the correct URL

          this.dispatchForm.reset();
        },
        (err: any) => {
          console.log('Error creating dispatch:', err);
        }
      );
    } else {
      console.log('Form is invalid');
    }
  }
}
















// import { Component, OnInit } from '@angular/core';
// import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
// import { ActivatedRoute } from '@angular/router';
// import { DispatchService } from '@app/shared/services/dispatch.service';
// import { RepeatOrderService } from '@app/shared/services/repeat-order.service';
// import { OrderDataService } from '@app/shared/services/order-data.service';
// import { forkJoin } from 'rxjs';

// interface Dispatch {
//   dispatched: number;
//   kgsBal: number;
// }

// @Component({
//   selector: 'app-dispatch-from',
//   templateUrl: './dispatch-from.component.html',
//   styleUrls: ['./dispatch-from.component.css']
// })
// export class DispatchFromComponent implements OnInit {
//   dispatchForm: FormGroup;
//   dispatchId: string;

//   constructor(
//     private fb: FormBuilder,
//     private route: ActivatedRoute,
//     private dispatchService: DispatchService,
//     private repeatOrderService: RepeatOrderService,
//     private orderDataService: OrderDataService
//   ) { }

//   ngOnInit(): void {
//     // Get the ID from the route parameters
//     this.route.paramMap.subscribe(params => {
//       this.dispatchId = params.get('id');
//       this.loadDispatchData();
//     });

//     this.initializeForm();
//   }

//   initializeForm(): void {
//     this.dispatchForm = this.fb.group({
//       orderKgs: [null, Validators.required],
//       poNo: [null, Validators.required],
//       clientName: [null, Validators.required],
//       ticketNo: [null, Validators.required],
//       dispatched: [null, Validators.required],
//       kgsBal: [null, Validators.required],
//       orderPc: [null, Validators.required],
//       orderNo: [null, Validators.required],
//       refNo: [null, Validators.required],
//       jobName: [null, Validators.required],
//       poNumber: [null, Validators.required],
//       dispatches: this.fb.array([this.createDispatch()]), // Initialize with one dispatch
//       dispatchDates: this.fb.array([this.createDispatchDate()]) // Initialize with one dispatch date
//     });

//     // Listen for changes to dispatches and recalculate dispatched and kgsBal
//     this.dispatches.valueChanges.subscribe(() => {
//       this.calculateDispatchedAndKgsBal();
      
//     });
//   }

//   // loadDispatchdispatchAndkgsbal(): void {
//   //   this.dispatchService.getDispatchBydispatchId(this.dispatchId).subscribe({
//   //     next: (dispatchData: Dispatch) => {
//   //       if (dispatchData) {
//   //         const { dispatched, kgsBal } = dispatchData;
//   //         const orderKgs = this.dispatchForm.get('orderKgs')?.value || 0;
  
//   //         // Update the dispatched and kgsBal fields in the form with initial values
//   //         this.dispatchForm.patchValue({
//   //           dispatched: dispatched || 0,
//   //           kgsBal: kgsBal || 0
//   //         });
  
//   //         // Listen to changes in the dispatch values and dynamically update dispatched and kgsBal
//   //         this.dispatches.valueChanges.subscribe(() => {
//   //           // Recalculate the sum of all dispatched values (from the form)
//   //           const dispatchedSum = this.dispatches.controls.reduce((sum, dispatchGroup: FormGroup) => {
//   //             const dispValue = dispatchGroup.get('disp')?.value || 0;
//   //             return sum + dispValue;
//   //           }, 0);
  
//   //           // Calculate the total dispatched value
//   //           const newDispatchBal = dispatched + dispatchedSum;
  
//   //           // Patch the form with the new dispatched value
//   //           this.dispatchForm.patchValue({ dispatched: newDispatchBal });
//   //           console.log('Updated dispatched: ', newDispatchBal);
  
//   //           // Calculate the remaining balance (kgsBal)
//   //           let remainingBalance = orderKgs - newDispatchBal;
  
//   //           // Patch the form with the remaining balance
//   //           this.dispatchForm.patchValue({ kgsBal: remainingBalance });
//   //           console.log('Remaining balance: ', remainingBalance);
//   //           // Check if dispatched exceeds available kgsBal and warn the user
//   //           if (newDispatchBal > orderKgs) {
//   //             alert('Dispatched quantity exceeds available balance!');
//   //           }
  
          
//   //         });
//   //       } else {
//   //         console.error('No dispatch found with the given ID');
//   //       }
//   //     },
//   //     error: (err) => {
//   //       console.error('Error fetching dispatch data', err);
//   //     }
//   //   });
//   // }
  
  // loadDispatchdispatchAndkgsbal(): void {
  //   this.dispatchService.getDispatchBydispatchId(this.dispatchId).subscribe({
  //     next: (dispatchData: Dispatch) => {
  //       if (dispatchData) {
  //         const { dispatched, kgsBal } = dispatchData;
  //         const orderKgs = this.dispatchForm.get('orderKgs')?.value || 0;
  
  //         // Update the dispatched and kgsBal fields in the form with initial values
  //         this.dispatchForm.patchValue({
  //           dispatched: dispatched || 0,
  //           kgsBal: kgsBal || 0
  //         });
  
  //         // Listen to changes in the dispatch values and dynamically update dispatched and kgsBal
  //         this.dispatches.valueChanges.subscribe(() => {
  //           // Recalculate the sum of all dispatched values (from the form)
  //           const dispatchedSum = this.dispatches.controls.reduce((sum, dispatchGroup: FormGroup) => {
  //             const dispValue = dispatchGroup.get('disp')?.value || 0;
  //             return sum + dispValue;
  //           }, 0);
  
  //           // Calculate the new dispatched value after adding the new dispatches
  //           const newDispatchBal = dispatched + dispatchedSum;
  
  //           // Patch the form with the new dispatched value
  //           this.dispatchForm.patchValue({ dispatched: newDispatchBal });
  //           console.log('Updated dispatched: ', newDispatchBal);
  
  //           // Calculate the remaining balance (kgsBal)
  //           let remainingBalance = orderKgs - newDispatchBal;
  
  //           // Patch the form with the remaining balance
  //           this.dispatchForm.patchValue({ kgsBal: remainingBalance });
  //           console.log('Remaining balance: ', remainingBalance);
  
  //           // Check if dispatched exceeds available kgsBal and warn the user
  //           if (newDispatchBal > orderKgs) {
  //             alert('Dispatched quantity exceeds available balance!');
  //           }
  //         });
  //       } else {
  //         console.error('No dispatch found with the given ID');
  //       }
  //     },
  //     error: (err) => {
  //       console.error('Error fetching dispatch data', err);
  //     }
  //   });
  // }
  
  
  
  
  
  
//   loadDispatchData(): void {
//     forkJoin({
//       repeatOrders: this.repeatOrderService.getAllRepeatOrder(),
//       unsavedOrders: this.orderDataService.getAllUnsavedOrders()
//     }).subscribe({
//       next: (data) => {
//         const repeatOrders = Array.isArray(data.repeatOrders) ? data.repeatOrders : [];
//         const unsavedOrders = Array.isArray(data.unsavedOrders) ? data.unsavedOrders : [];
//         let dispatchData = repeatOrders.find(order => order._id === this.dispatchId) ||
//                            unsavedOrders.find(order => order._id === this.dispatchId);

//         if (dispatchData) {
//           console.log('+',dispatchData);
//           this.populateForm(dispatchData);
// //this. loadDispatchdispatchAndkgsbal();
//           // Check if the order is completed
//           if (dispatchData.status === 'Closed') {
//             alert('This order has already been completed!');
//             this.dispatchForm.disable(); // Disable the form if completed
//           }
//         } else {
//           console.error('No dispatch found with the given ID');
//         }
//       },
//       error: (err) => {
//         console.error('Error fetching dispatch data', err);
//       }
//     });
//   }

//   populateForm(dispatchData: any): void {
//     this.dispatchForm.patchValue({
//       orderKgs: dispatchData.orderKgs,
//       clientName: dispatchData.clientName,
//       poNo: dispatchData.poNo,
//       ticketNo: dispatchData.ticketno,
//       orderPc: dispatchData.orderPcs || 0,
//       orderNo: dispatchData.orderId || dispatchData.jssId,
//       refNo: dispatchData.refNo,
//       jobName: dispatchData.jobName,
//       poNumber: dispatchData.poNo
//     });

//     // Populate dispatches if available
//     if (dispatchData.dispatches && dispatchData.dispatches.length) {
//       dispatchData.dispatches.forEach(dispatch => {
//         this.addDispatch(); // Add new dispatch dynamically
//       });
//     }

//     // Calculate dispatched and kgsBal initially if there are dispatches
//     this.calculateDispatchedAndKgsBal();
//     this.loadDispatchdispatchAndkgsbal()
    
//   }

//   createDispatch(): FormGroup {
//     return this.fb.group({
//       disp: [null, Validators.required]
//     });
//   }

//   createDispatchDate(): FormGroup {
//     return this.fb.group({
//       dispatchDate: [null, Validators.required]
//     });
//   }

//   get dispatches() {
//     return this.dispatchForm.get('dispatches') as FormArray;
//   }

//   get dispatchDates() {
//     return this.dispatchForm.get('dispatchDates') as FormArray;
//   }

//   addDispatch(): void {
//     this.dispatches.push(this.createDispatch());
//     this.dispatchDates.push(this.createDispatchDate());
//   }

//   removeDispatchDate(): void {
//     if (this.dispatches.length > 1) {
//       this.dispatches.removeAt(this.dispatches.length - 1);
//       this.dispatchDates.removeAt(this.dispatchDates.length - 1);
//     } else {
//       alert('At least one dispatch pair is required.');
//     }
//   }

  // calculateDispatchedAndKgsBal(): void {
  //   // Calculate the sum of all dispatched values
  //   const dispatchedSum = this.dispatches.controls.reduce((sum, dispatchGroup: FormGroup) => {
  //     const dispValue = dispatchGroup.get('disp')?.value || 0;
  //     return sum + dispValue;
  //   }, 0);

  //   // Update dispatched field
  //   this.dispatchForm.patchValue({ dispatched: dispatchedSum });

  //   // Calculate kgsBal
  //   const orderKgs = this.dispatchForm.get('orderKgs')?.value || 0;
  //   const kgsBal = orderKgs - dispatchedSum;

  //   // Update kgsBal field
  //   this.dispatchForm.patchValue({ kgsBal: kgsBal });

   
   
  // }
  

  
  
  
  // onSubmit(): void {
  //   if (this.dispatchForm.valid) {
  //     // Add dispatchId to the form data to be sent to the backend
  //     const formData = {
  //       ...this.dispatchForm.value,
  //     };
  
  //     console.log('Form Data to be submitted: ', formData);
  
  //     // Send the form data along with the dispatchId to the service
  //     this.dispatchService.addDispatch(formData, this.dispatchId).subscribe(
  //       (res: any) => {
  //         console.log('Dispatch created successfully:', res);
  //         this.dispatchForm.reset();
  //       },
  //       (err: any) => {
  //         console.log('Error creating dispatch:', err);
  //       }
  //     );
  //   } else {
  //     console.log('Form is invalid');
  //   }
  // }
  

  
  
// }